import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, RefreshCw, TrendingUp, DollarSign, BarChart3, Download, AlertTriangle, CheckCircle, Info, Upload } from 'lucide-react';
import api from '../api/client';
import { PageHeader } from '../components/FilterBar';

const fmtLKR = (v) => v != null ? `LKR ${Number(Math.round(parseFloat(v||0))).toLocaleString()}` : '—';
const fmtM = (v) => v != null && parseFloat(v) > 0 ? `${(parseFloat(v||0)/1000000).toFixed(2)}M` : '—';
const pct = (a, b) => b > 0 ? Math.round(parseFloat(a||0)/parseFloat(b)*100) : 0;

const EXP_TYPES = ['OPEX','CAPEX'];
const CATEGORIES = ['Software','Hardware'];
const NATURES = ['Renewal','New Purchase','Repairs','Upgrade','Replacements','Other'];
const SECTION_COLORS = {
  'OPEX-Software': '#1e40af',
  'OPEX-Hardware': '#065f46',
  'CAPEX-Software': '#6b21a8',
  'CAPEX-Hardware': '#92400e',
};

const OUT_OF_BUDGET = '__extra__';

function KpiCard({ label, value, sub, color, icon: Icon, badge }) {
  return (
    <div className="glass glow-border rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} style={{ color }}/>}
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color:'#475569' }}>{label}</span>
        </div>
        {badge}
      </div>
      <div className="text-xl font-bold" style={{ color }}>{value}</div>
      {sub && <div className="text-xs mt-1" style={{ color:'#334155' }}>{sub}</div>}
    </div>
  );
}

function ProgressBar({ value, max, color, showLabel = true }) {
  const p = max > 0 ? Math.min(100, Math.round(parseFloat(value||0)/parseFloat(max)*100)) : 0;
  const over = parseFloat(value||0) > parseFloat(max||0) && max > 0;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
      <div style={{ flex:1, height:5, background:'rgba(255,255,255,0.06)', borderRadius:3, overflow:'hidden' }}>
        <div style={{ width:`${Math.min(p,100)}%`, height:'100%', background: over?'#ef4444':p>75?'#f97316':color, borderRadius:3, transition:'width 0.4s ease' }}/>
      </div>
      {showLabel && <span style={{ fontSize:10, color: over?'#f87171':'#94a3b8', fontWeight:600, minWidth:32 }}>{p}%</span>}
    </div>
  );
}

function OverBudgetBadge() {
  return <span style={{ fontSize:9, padding:'2px 5px', borderRadius:3, background:'rgba(239,68,68,0.15)', color:'#f87171', fontWeight:700 }}>OVER</span>;
}

export default function ITBudget() {
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [items, setItems] = useState([]);
  const [actuals, setActuals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newYearModal, setNewYearModal] = useState(false);
  const [newYearForm, setNewYearForm] = useState({ fy_label:'', fy_start:'', fy_end:'', notes:'' });
  const [backfillModal, setBackfillModal] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const loadYears = useCallback(async () => {
    const r = await api.get('/budget/years');
    setYears(r.data);
    if (!selectedYear && r.data.length) setSelectedYear(r.data[0].id);
  }, [selectedYear]);

  useEffect(() => { loadYears(); }, []);

  const loadData = useCallback(async () => {
    if (!selectedYear) return;
    setLoading(true);
    try {
      const [itemsR, actualsR, summaryR] = await Promise.all([
        api.get(`/budget/years/${selectedYear}/items`),
        api.get(`/budget/years/${selectedYear}/actuals`),
        api.get(`/budget/years/${selectedYear}/summary`),
      ]);
      setItems(itemsR.data);
      setActuals(actualsR.data);
      setSummary(summaryR.data);
    } finally { setLoading(false); }
  }, [selectedYear]);

  useEffect(() => { loadData(); }, [loadData]);

  const openBoardPresentation = () => {
    const token = localStorage.getItem('token');
    const win = window.open('', '_blank');
    if (win) win.location.href = `/api/budget/years/${selectedYear}/board-presentation?token=${token}`;
  };

  const openItemModal = (item = null) => {
    setForm(item ? { ...item } : { expenditure_type:'OPEX', category:'Software', item:'', nature_of_expense:'Renewal', budget_fy_total_lkr:'', budget_q_total_lkr:'', qty:'', unit_price_lkr:'', business_justification:'', year_id: selectedYear });
    setModal('item');
    setError('');
  };

  const openActualModal = (actual = null) => {
    const base = actual ? { ...actual } : { year_id: selectedYear, description:'', amount_lkr:'', expense_date: new Date().toISOString().split('T')[0], expenditure_type:'OPEX', category:'Software', budget_item_id:'', notes:'' };
    setForm(base);
    setModal('actual');
    setError('');
  };

  const saveItem = async () => {
    if (!form.item) { setError('Item name is required'); return; }
    setSaving(true); setError('');
    try {
      if (form.id) await api.put(`/budget/items/${form.id}`, form);
      else await api.post('/budget/items', { ...form, year_id: selectedYear });
      setModal(null); loadData();
    } catch (e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const saveActual = async () => {
    if (!form.description) { setError('Description is required'); return; }
    if (!form.expense_date) { setError('Expense date is required'); return; }
    if (!form.budget_item_id) { setError('Please link to a budget item or choose "Out of Budget / Extra"'); return; }
    setSaving(true); setError('');
    try {
      const isExtra = form.budget_item_id === OUT_OF_BUDGET;
      const payload = {
        ...form,
        budget_item_id: isExtra ? null : form.budget_item_id,
        source_type: isExtra ? 'extra' : (form.source_type === 'asset' || form.source_type === 'accessory' ? form.source_type : 'manual'),
      };
      if (form.id) await api.put(`/budget/actuals/${form.id}`, payload);
      else await api.post('/budget/actuals', payload);
      setModal(null); loadData();
    } catch (e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this budget item?')) return;
    await api.delete(`/budget/items/${id}`); loadData();
  };

  const deleteActual = async (id) => {
    if (!window.confirm('Delete this actual expense?')) return;
    await api.delete(`/budget/actuals/${id}`); loadData();
  };

  const createYear = async () => {
    if (!newYearForm.fy_label || !newYearForm.fy_start || !newYearForm.fy_end) return;
    await api.post('/budget/years', newYearForm);
    setNewYearModal(false); loadYears();
  };

  const openBackfill = async () => {
    setBackfillResult(null);
    setBackfillLoading(true);
    setBackfillModal(true);
    try {
      const r = await api.get('/budget/backfill-preview');
      setBackfillPreview(r.data);
    } catch (e) {
      setBackfillPreview({ error: e.response?.data?.error || 'Preview failed' });
    } finally { setBackfillLoading(false); }
  };

  const runBackfill = async () => {
    setBackfillLoading(true);
    try {
      const r = await api.post('/budget/backfill');
      setBackfillResult(r.data);
      loadData();
    } catch (e) {
      setBackfillResult({ error: e.response?.data?.error || 'Backfill failed' });
    } finally { setBackfillLoading(false); }
  };

  const totals = summary?.totals || {};
  const summaryItems = summary?.items || [];
  const currentYear = years.find(y => y.id === selectedYear);

  const sections = [
    { key:'OPEX-Software', label:'OPEX — Software', budgetKey:'opex_sw_budget' },
    { key:'OPEX-Hardware', label:'OPEX — Hardware', budgetKey:'opex_hw_budget' },
    { key:'CAPEX-Software', label:'CAPEX — Software', budgetKey:'capex_sw_budget' },
    { key:'CAPEX-Hardware', label:'CAPEX — Hardware', budgetKey:'capex_hw_budget' },
  ];

  const TABS = [
    { key:'overview', label:'Overview' },
    { key:'items', label:'Budget Items' },
    { key:'actuals', label:'Actuals' },
    { key:'vs-actual', label:'Budget vs Actual' },
    { key:'historical', label:'Historical' },
  ];

  const f = (k) => form[k] ?? '';
  const sf = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inp = "input-dark";

  // Derived insights for Overview
  const overBudgetItems = summaryItems.filter(i => parseFloat(i.actual_total||0) > parseFloat(i.budget_fy_total_lkr||0) && parseFloat(i.budget_fy_total_lkr||0) > 0);
  const nearBudgetItems = summaryItems.filter(i => {
    const p2 = pct(i.actual_total, i.budget_fy_total_lkr);
    return p2 >= 75 && p2 <= 100;
  });
  const grandBudget = parseFloat(totals.grand_budget||0);
  const grandActual = parseFloat(totals.grand_actual||0);
  const remaining = grandBudget - grandActual;
  const assetActual = parseFloat(totals.asset_actual||0);
  const manualActual = parseFloat(totals.manual_actual||0);
  const unlinkedCount = parseInt(totals.unlinked_count||0);

  // Top 5 spenders
  const top5 = [...summaryItems].sort((a,b) => parseFloat(b.actual_total||0)-parseFloat(a.actual_total||0)).slice(0,5);

  return (
    <div className="space-y-4">
      <PageHeader title="IT Budget">
        <div className="flex gap-2 flex-wrap items-center">
          <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)} className="input-dark" style={{ minWidth:120 }}>
            {years.map(y => <option key={y.id} value={y.id}>FY {y.fy_label}</option>)}
          </select>
          <button onClick={() => setNewYearModal(true)} className="btn-ghost text-xs"><Plus size={12}/> New FY</button>
          <button onClick={openBoardPresentation} className="btn-ghost text-xs" style={{ color:'#818cf8' }}>
            <Download size={12}/> Board Presentation
          </button>
          <button onClick={loadData} className="btn-ghost p-2"><RefreshCw size={13} className={loading?'animate-spin':''}/></button>
        </div>
      </PageHeader>

      {/* Tab bar */}
      <div className="flex gap-1 p-1 rounded-xl overflow-x-auto" style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.06)', width:'max-content' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap"
            style={{ background:tab===t.key?'rgba(59,130,246,0.2)':'transparent', color:tab===t.key?'#60a5fa':'#475569', border:tab===t.key?'1px solid rgba(59,130,246,0.3)':'1px solid transparent' }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <div className="w-6 h-6 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/>
        </div>
      )}

      {/* ── OVERVIEW ─────────────────────────────────────── */}
      {tab==='overview' && !loading && summary && (
        <div className="space-y-4">
          {/* Alerts row */}
          {overBudgetItems.length > 0 && (
            <div className="rounded-xl p-3 flex items-start gap-3" style={{ background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.25)' }}>
              <AlertTriangle size={15} style={{ color:'#f87171', flexShrink:0, marginTop:1 }}/>
              <div>
                <span className="text-xs font-semibold" style={{ color:'#f87171' }}>Over Budget — {overBudgetItems.length} item{overBudgetItems.length>1?'s':''}: </span>
                <span className="text-xs" style={{ color:'#94a3b8' }}>{overBudgetItems.map(i=>i.item).join(', ')}</span>
              </div>
            </div>
          )}
          {nearBudgetItems.length > 0 && overBudgetItems.length === 0 && (
            <div className="rounded-xl p-3 flex items-start gap-3" style={{ background:'rgba(249,115,22,0.08)', border:'1px solid rgba(249,115,22,0.2)' }}>
              <Info size={15} style={{ color:'#fb923c', flexShrink:0, marginTop:1 }}/>
              <span className="text-xs" style={{ color:'#94a3b8' }}>{nearBudgetItems.length} item{nearBudgetItems.length>1?'s':''} near budget limit (≥75%): {nearBudgetItems.map(i=>i.item).join(', ')}</span>
            </div>
          )}

          {/* Section KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {sections.map(s => {
              const sItems = summaryItems.filter(i => `${i.expenditure_type}-${i.category}` === s.key);
              const sBudget = sItems.reduce((sum,i) => sum+parseFloat(i.budget_fy_total_lkr||0), 0);
              const sActual = sItems.reduce((sum,i) => sum+parseFloat(i.actual_total||0), 0);
              const over = sActual > sBudget && sBudget > 0;
              const color = SECTION_COLORS[s.key];
              return (
                <div key={s.key} className="glass glow-border rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold" style={{ color }}>{s.label}</span>
                    {over && <OverBudgetBadge/>}
                  </div>
                  <div className="text-base font-bold mb-0.5" style={{ color:'#e2e8f0' }}>{fmtLKR(sBudget)}</div>
                  <div className="text-xs mb-2" style={{ color: over?'#f87171':'#94a3b8' }}>Spent: {fmtLKR(sActual)}</div>
                  <ProgressBar value={sActual} max={sBudget} color={color}/>
                  <div className="text-xs mt-1.5" style={{ color:'#334155' }}>{sItems.length} items · {pct(sActual, sBudget)}% utilized</div>
                </div>
              );
            })}
          </div>

          {/* Grand total banner */}
          <div className="glass glow-border rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-base font-bold" style={{ color:'#e2e8f0' }}>FY {currentYear?.fy_label} — Total Budget</div>
                <div className="text-xs mt-0.5" style={{ color:'#334155' }}>{currentYear?.fy_start} to {currentYear?.fy_end}</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold" style={{ color:'#60a5fa' }}>{fmtLKR(grandBudget)}</div>
                <div className="text-xs mt-0.5" style={{ color: grandActual>grandBudget?'#f87171':'#94a3b8' }}>
                  Spent: {fmtLKR(grandActual)} ({pct(grandActual, grandBudget)}%)
                </div>
              </div>
            </div>
            <ProgressBar value={grandActual} max={grandBudget} color="#3b82f6"/>
            <div className="flex gap-4 mt-3">
              <div className="flex items-center gap-1.5"><span className="text-xs" style={{ color:'#475569' }}>Remaining:</span><span className="text-xs font-bold" style={{ color: remaining<0?'#f87171':'#34d399' }}>{fmtLKR(Math.abs(remaining))}{remaining<0?' over':''}</span></div>
              <div className="flex items-center gap-1.5"><span className="text-xs" style={{ color:'#475569' }}>Utilization:</span><span className="text-xs font-bold" style={{ color:'#e2e8f0' }}>{pct(grandActual, grandBudget)}%</span></div>
            </div>
          </div>

          {/* Actuals breakdown + Top spenders side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Actuals source breakdown */}
            <div className="glass glow-border rounded-2xl p-5">
              <div className="text-sm font-semibold mb-4" style={{ color:'#94a3b8' }}>Actuals Breakdown</div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-xl" style={{ background:'rgba(99,102,241,0.08)', border:'1px solid rgba(99,102,241,0.15)' }}>
                  <div className="flex items-center gap-2">
                    <span style={{ fontSize:16 }}>🖥</span>
                    <div>
                      <div className="text-xs font-semibold" style={{ color:'#818cf8' }}>Auto-linked from Assets</div>
                      <div className="text-xs" style={{ color:'#334155' }}>Created automatically when assets were added</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold" style={{ color:'#818cf8' }}>{fmtLKR(assetActual)}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl" style={{ background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.15)' }}>
                  <div className="flex items-center gap-2">
                    <span style={{ fontSize:16 }}>✏️</span>
                    <div>
                      <div className="text-xs font-semibold" style={{ color:'#34d399' }}>Manually Entered</div>
                      <div className="text-xs" style={{ color:'#334155' }}>Renewals, payments, services</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold" style={{ color:'#34d399' }}>{fmtLKR(manualActual)}</div>
                  </div>
                </div>
                {unlinkedCount > 0 && (
                  <div className="flex items-center justify-between p-3 rounded-xl" style={{ background:'rgba(249,115,22,0.08)', border:'1px solid rgba(249,115,22,0.15)' }}>
                    <div className="flex items-center gap-2">
                      <span style={{ fontSize:16 }}>⚠️</span>
                      <div>
                        <div className="text-xs font-semibold" style={{ color:'#fb923c' }}>Out of Budget / Extra</div>
                        <div className="text-xs" style={{ color:'#334155' }}>{unlinkedCount} unbudgeted expense{unlinkedCount>1?'s':''}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-semibold" style={{ color:'#fb923c' }}>{unlinkedCount} items</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Top spenders */}
            <div className="glass glow-border rounded-2xl p-5">
              <div className="text-sm font-semibold mb-4" style={{ color:'#94a3b8' }}>Top 5 Spending Items</div>
              {top5.length === 0 ? (
                <div className="text-xs text-center py-6" style={{ color:'#334155' }}>No actuals recorded yet</div>
              ) : (
                <div className="space-y-2.5">
                  {top5.map((item, idx) => {
                    const itemActual = parseFloat(item.actual_total||0);
                    const itemBudget = parseFloat(item.budget_fy_total_lkr||0);
                    const over = itemActual > itemBudget && itemBudget > 0;
                    return (
                      <div key={item.id}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span style={{ fontSize:10, fontWeight:700, color:'#334155', minWidth:14 }}>#{idx+1}</span>
                            <span className="text-xs font-medium truncate" style={{ color:'#e2e8f0', maxWidth:160 }}>{item.item}</span>
                            {over && <OverBudgetBadge/>}
                          </div>
                          <span className="text-xs font-bold" style={{ color: over?'#f87171':'#60a5fa' }}>{fmtLKR(itemActual)}</span>
                        </div>
                        <ProgressBar value={itemActual} max={itemBudget||itemActual} color={SECTION_COLORS[`${item.expenditure_type}-${item.category}`]||'#3b82f6'} showLabel={false}/>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Over-budget item detail table */}
          {overBudgetItems.length > 0 && (
            <div className="glass glow-border rounded-2xl overflow-hidden">
              <div className="px-5 py-3" style={{ background:'rgba(239,68,68,0.08)', borderBottom:'1px solid rgba(239,68,68,0.2)' }}>
                <span className="text-sm font-bold" style={{ color:'#f87171' }}>⚠ Over-Budget Items — Action Required</span>
              </div>
              <div className="overflow-x-auto">
                <table style={{ width:'100%', fontSize:12, borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
                      {['Item','Section','Budget','Actual','Over By'].map(h => (
                        <th key={h} style={{ padding:'8px 12px', textAlign: h==='Item'?'left':'right', color:'#334155', fontWeight:700, fontSize:10, textTransform:'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {overBudgetItems.map(item => {
                      const b = parseFloat(item.budget_fy_total_lkr||0);
                      const a = parseFloat(item.actual_total||0);
                      return (
                        <tr key={item.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                          <td style={{ padding:'8px 12px', color:'#e2e8f0', fontWeight:500 }}>{item.item}</td>
                          <td style={{ padding:'8px 12px', color:'#94a3b8', textAlign:'right' }}>{item.expenditure_type} {item.category}</td>
                          <td style={{ padding:'8px 12px', color:'#60a5fa', textAlign:'right', fontWeight:600 }}>{fmtLKR(b)}</td>
                          <td style={{ padding:'8px 12px', color:'#f87171', textAlign:'right', fontWeight:600 }}>{fmtLKR(a)}</td>
                          <td style={{ padding:'8px 12px', color:'#f87171', textAlign:'right', fontWeight:700 }}>{fmtLKR(a-b)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── BUDGET ITEMS ──────────────────────────────────── */}
      {tab==='items' && !loading && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => openItemModal()} className="btn-primary text-xs"><Plus size={12}/> Add Item</button>
          </div>
          {sections.map(s => {
            const sItems = items.filter(i => `${i.expenditure_type}-${i.category}` === s.key);
            if (!sItems.length) return null;
            const color = SECTION_COLORS[s.key];
            const sBudget = sItems.reduce((sum,i) => sum+parseFloat(i.budget_fy_total_lkr||0), 0);
            const sActual = sItems.reduce((sum,i) => sum+parseFloat(i.actual_total||0), 0);
            return (
              <div key={s.key} className="glass glow-border rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3" style={{ background:`${color}20`, borderBottom:`1px solid ${color}40` }}>
                  <span className="text-sm font-bold" style={{ color }}>{s.label}</span>
                  <span className="text-xs font-semibold" style={{ color:'#94a3b8' }}>Budget: {fmtLKR(sBudget)} · Actual: {fmtLKR(sActual)} · {pct(sActual,sBudget)}%</span>
                </div>
                <div className="overflow-x-auto">
                  <table style={{ width:'100%', fontSize:12, borderCollapse:'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
                        {['Item','Nature','Qty','Unit Price (LKR)','Q Total (LKR)','FY Total (LKR)','Actual','Justification',''].map(h => (
                          <th key={h} style={{ padding:'8px 12px', textAlign:'left', color:'#334155', fontWeight:700, fontSize:10, textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sItems.map(item => {
                        const over = parseFloat(item.actual_total||0) > parseFloat(item.budget_fy_total_lkr||0);
                        return (
                          <tr key={item.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)', background: over?'rgba(239,68,68,0.04)':'transparent' }}
                            onMouseEnter={e=>e.currentTarget.style.background=over?'rgba(239,68,68,0.07)':'rgba(255,255,255,0.02)'}
                            onMouseLeave={e=>e.currentTarget.style.background=over?'rgba(239,68,68,0.04)':'transparent'}>
                            <td style={{ padding:'8px 12px', color:'#e2e8f0', fontWeight:500 }}>{item.item}{over&&<span style={{marginLeft:5,fontSize:9,color:'#f87171',fontWeight:700}}>OVER</span>}</td>
                            <td style={{ padding:'8px 12px', color:'#94a3b8' }}>{item.nature_of_expense||'—'}</td>
                            <td style={{ padding:'8px 12px', color:'#94a3b8', textAlign:'center' }}>{item.qty||'—'}</td>
                            <td style={{ padding:'8px 12px', color:'#94a3b8', textAlign:'right' }}>{item.unit_price_lkr ? Number(Math.round(item.unit_price_lkr)).toLocaleString() : '—'}</td>
                            <td style={{ padding:'8px 12px', color:'#60a5fa', textAlign:'right' }}>{Number(Math.round(item.budget_q_total_lkr||0)).toLocaleString()}</td>
                            <td style={{ padding:'8px 12px', fontWeight:700, color:'#60a5fa', textAlign:'right' }}>{Number(Math.round(item.budget_fy_total_lkr||0)).toLocaleString()}</td>
                            <td style={{ padding:'8px 12px', fontWeight:600, color: over?'#f87171':'#34d399', textAlign:'right' }}>{Number(Math.round(item.actual_total||0)).toLocaleString()}</td>
                            <td style={{ padding:'8px 12px', color:'#475569', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.business_justification||'—'}</td>
                            <td style={{ padding:'8px 12px', whiteSpace:'nowrap' }}>
                              <div style={{ display:'flex', gap:6 }}>
                                <button onClick={() => openItemModal(item)} style={{ background:'none', border:'none', cursor:'pointer', color:'#60a5fa' }}><Edit2 size={13}/></button>
                                <button onClick={() => deleteItem(item.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#f87171' }}><Trash2 size={13}/></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
          {!items.length && <div className="text-center py-10 text-xs" style={{ color:'#334155' }}>No budget items yet. Click "Add Item" to start.</div>}
        </div>
      )}

      {/* ── ACTUALS ───────────────────────────────────────── */}
      {tab==='actuals' && !loading && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm" style={{ color:'#334155' }}>{actuals.length} actual expense records</div>
            <div className="flex gap-2">
              <button onClick={openBackfill} className="btn-ghost text-xs" style={{ color:'#818cf8' }}>
                <Upload size={12}/> Import from Assets & Accessories
              </button>
              <button onClick={() => openActualModal()} className="btn-primary text-xs"><Plus size={12}/> Add Actual</button>
            </div>
          </div>
          {/* Info banner about auto-linking */}
          <div className="rounded-xl p-3 flex items-start gap-3" style={{ background:'rgba(99,102,241,0.08)', border:'1px solid rgba(99,102,241,0.15)' }}>
            <Info size={14} style={{ color:'#818cf8', flexShrink:0, marginTop:1 }}/>
            <span className="text-xs" style={{ color:'#94a3b8' }}>
              <strong style={{ color:'#818cf8' }}>Auto-linked from Assets:</strong> When you add an asset in All Assets with a purchase price, it automatically creates an actual here. No need to add it manually.
            </span>
          </div>
          <div className="glass glow-border rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table style={{ width:'100%', fontSize:12, borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
                    {['Date','Description','Type','Category','Linked Item','Amount (LKR)','Source','Notes',''].map(h => (
                      <th key={h} style={{ padding:'8px 12px', textAlign:'left', color:'#334155', fontWeight:700, fontSize:10, textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {actuals.map(a => (
                    <tr key={a.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.02)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={{ padding:'8px 12px', color:'#475569', whiteSpace:'nowrap' }}>{a.expense_date}</td>
                      <td style={{ padding:'8px 12px', color:'#e2e8f0', fontWeight:500 }}>{a.description}</td>
                      <td style={{ padding:'8px 12px', color:'#94a3b8' }}>{a.expenditure_type}</td>
                      <td style={{ padding:'8px 12px', color:'#94a3b8' }}>{a.category}</td>
                      <td style={{ padding:'8px 12px', color:'#60a5fa', fontSize:11 }}>
                        {a.budget_item_name || <span style={{ color: a.source_type==='extra'?'#fb923c':'#334155' }}>{a.source_type==='extra'?'Out of Budget':'Unlinked'}</span>}
                      </td>
                      <td style={{ padding:'8px 12px', fontWeight:700, color:'#34d399', textAlign:'right' }}>{Number(Math.round(a.amount_lkr||0)).toLocaleString()}</td>
                      <td style={{ padding:'8px 12px' }}>
                        <span style={{ fontSize:10, padding:'2px 6px', borderRadius:4,
                          background: a.source_type==='asset'?'rgba(99,102,241,0.2)':a.source_type==='accessory'?'rgba(16,185,129,0.2)':a.source_type==='extra'?'rgba(249,115,22,0.15)':'rgba(255,255,255,0.06)',
                          color: a.source_type==='asset'?'#818cf8':a.source_type==='accessory'?'#34d399':a.source_type==='extra'?'#fb923c':'#475569' }}>
                          {a.source_type==='asset'?'🖥 Asset':a.source_type==='accessory'?'🔌 Accessory':a.source_type==='extra'?'⚠ Extra':'✏ Manual'}
                        </span>
                      </td>
                      <td style={{ padding:'8px 12px', color:'#475569', maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.notes||'—'}</td>
                      <td style={{ padding:'8px 12px', whiteSpace:'nowrap' }}>
                        <div style={{ display:'flex', gap:6 }}>
                          <button onClick={() => openActualModal(a)} style={{ background:'none', border:'none', cursor:'pointer', color:'#60a5fa' }}><Edit2 size={13}/></button>
                          <button onClick={() => deleteActual(a.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#f87171' }}><Trash2 size={13}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!actuals.length && (
                    <tr><td colSpan={9} style={{ padding:'24px', textAlign:'center', color:'#334155' }}>No actuals recorded yet. Add assets in All Assets (with purchase price) or click "Add Actual" for other expenses.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── BUDGET VS ACTUAL ──────────────────────────────── */}
      {tab==='vs-actual' && !loading && summary && (
        <div className="space-y-4">
          <div className="glass glow-border rounded-2xl p-5">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center"><div className="text-xs mb-1" style={{ color:'#475569' }}>Total Budget</div><div className="text-xl font-bold" style={{ color:'#60a5fa' }}>{fmtLKR(grandBudget)}</div></div>
              <div className="text-center"><div className="text-xs mb-1" style={{ color:'#475569' }}>Total Actual</div><div className="text-xl font-bold" style={{ color:'#34d399' }}>{fmtLKR(grandActual)}</div></div>
              <div className="text-center"><div className="text-xs mb-1" style={{ color:'#475569' }}>Variance (Remaining)</div><div className="text-xl font-bold" style={{ color: grandActual>grandBudget?'#f87171':'#34d399' }}>{fmtLKR(Math.abs(remaining))}</div></div>
            </div>
            <ProgressBar value={grandActual} max={grandBudget} color="#3b82f6"/>
          </div>

          {sections.map(s => {
            const sItems = summaryItems.filter(i => `${i.expenditure_type}-${i.category}` === s.key);
            if (!sItems.length) return null;
            const color = SECTION_COLORS[s.key];
            return (
              <div key={s.key} className="glass glow-border rounded-2xl overflow-hidden">
                <div className="px-5 py-3" style={{ background:`${color}20`, borderBottom:`1px solid ${color}40` }}>
                  <span className="text-sm font-bold" style={{ color }}>{s.label}</span>
                </div>
                <div className="overflow-x-auto">
                  <table style={{ width:'100%', fontSize:12, borderCollapse:'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
                        {['Item','Budget (FY)','Actual','Variance','Asset / Manual','Utilized'].map(h => (
                          <th key={h} style={{ padding:'8px 12px', textAlign: h==='Item'?'left':'right', color:'#334155', fontWeight:700, fontSize:10, textTransform:'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sItems.map(item => {
                        const budgeted = parseFloat(item.budget_fy_total_lkr||0);
                        const actual = parseFloat(item.actual_total||0);
                        const variance = budgeted - actual;
                        const over = actual > budgeted;
                        const assetAmt = parseFloat(item.asset_amount||0);
                        const manualAmt = parseFloat(item.manual_amount||0);
                        const assetCnt = parseInt(item.asset_count||0);
                        return (
                          <tr key={item.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)', background: over?'rgba(239,68,68,0.03)':'transparent' }}
                            onMouseEnter={e=>e.currentTarget.style.background=over?'rgba(239,68,68,0.07)':'rgba(255,255,255,0.02)'}
                            onMouseLeave={e=>e.currentTarget.style.background=over?'rgba(239,68,68,0.03)':'transparent'}>
                            <td style={{ padding:'8px 12px', color:'#e2e8f0', fontWeight:500 }}>{item.item}</td>
                            <td style={{ padding:'8px 12px', color:'#60a5fa', textAlign:'right', fontWeight:600 }}>{fmtLKR(budgeted)}</td>
                            <td style={{ padding:'8px 12px', color: over?'#f87171':'#34d399', textAlign:'right', fontWeight:600 }}>{fmtLKR(actual)}</td>
                            <td style={{ padding:'8px 12px', textAlign:'right', fontWeight:600, color: over?'#f87171':'#34d399' }}>{over?'Over by ':''}{fmtLKR(Math.abs(variance))}</td>
                            <td style={{ padding:'8px 12px', textAlign:'right' }}>
                              <div style={{ fontSize:10 }}>
                                {assetCnt > 0 && <span style={{ color:'#818cf8' }}>🖥 {assetCnt} asset{assetCnt>1?'s':''} ({fmtLKR(assetAmt)})</span>}
                                {assetCnt > 0 && manualAmt > 0 && <span style={{ color:'#334155' }}> · </span>}
                                {manualAmt > 0 && <span style={{ color:'#94a3b8' }}>✏ {fmtLKR(manualAmt)}</span>}
                                {assetCnt === 0 && manualAmt === 0 && <span style={{ color:'#334155' }}>—</span>}
                              </div>
                            </td>
                            <td style={{ padding:'8px 12px', minWidth:100 }}><ProgressBar value={actual} max={budgeted} color={color}/></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── HISTORICAL ───────────────────────────────────── */}
      {tab==='historical' && !loading && (
        <div className="glass glow-border rounded-2xl p-5">
          <div className="text-sm font-semibold mb-4" style={{ color:'#94a3b8' }}>Historical FY Comparison</div>
          <div className="overflow-x-auto">
            <table style={{ width:'100%', fontSize:12, borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
                  {['Financial Year','Period','Total Budget','Actual Spend','Variance','Utilization'].map(h => (
                    <th key={h} style={{ padding:'8px 12px', textAlign:'left', color:'#334155', fontWeight:700, fontSize:10, textTransform:'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {years.map(y => {
                  const budgeted = parseFloat(y.budgeted_total||0);
                  const actual = parseFloat(y.actual_total||0);
                  const variance = budgeted - actual;
                  const isCurrent = y.id === selectedYear;
                  return (
                    <tr key={y.id}
                      onClick={() => setSelectedYear(y.id)}
                      style={{ borderBottom:'1px solid rgba(255,255,255,0.04)', cursor:'pointer', background:isCurrent?'rgba(59,130,246,0.06)':'transparent' }}
                      onMouseEnter={e=>!isCurrent&&(e.currentTarget.style.background='rgba(255,255,255,0.02)')}
                      onMouseLeave={e=>!isCurrent&&(e.currentTarget.style.background='transparent')}>
                      <td style={{ padding:'8px 12px', color: isCurrent?'#60a5fa':'#e2e8f0', fontWeight: isCurrent?700:400 }}>FY {y.fy_label}{isCurrent?' ← current':''}</td>
                      <td style={{ padding:'8px 12px', color:'#475569', fontSize:11 }}>{y.fy_start} — {y.fy_end}</td>
                      <td style={{ padding:'8px 12px', color:'#60a5fa', fontWeight:600 }}>{fmtLKR(budgeted)}</td>
                      <td style={{ padding:'8px 12px', color:'#34d399', fontWeight:600 }}>{fmtLKR(actual)}</td>
                      <td style={{ padding:'8px 12px', color:actual>budgeted?'#f87171':'#34d399', fontWeight:600 }}>{fmtLKR(Math.abs(variance))}</td>
                      <td style={{ padding:'8px 12px', minWidth:120 }}><ProgressBar value={actual} max={budgeted} color="#3b82f6"/></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-xs mt-3" style={{ color:'#334155' }}>Click any row to switch to that FY.</div>
        </div>
      )}

      {/* ── MODALS ──────────────────────────────────────── */}
      {modal==='item' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth:560 }}>
            <h2 className="text-base font-bold mb-4" style={{ color:'#e2e8f0' }}>{f('id') ? 'Edit' : 'Add'} Budget Item</h2>
            {error && <div className="mb-3 p-2 rounded-lg text-xs" style={{ background:'rgba(239,68,68,0.1)', color:'#f87171' }}>{error}</div>}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Type</label>
                  <select value={f('expenditure_type')} onChange={e=>sf('expenditure_type',e.target.value)} className={inp}>
                    {EXP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Category</label>
                  <select value={f('category')} onChange={e=>sf('category',e.target.value)} className={inp}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color:'#475569' }}>Item Name *</label>
                <input value={f('item')} onChange={e=>sf('item',e.target.value)} className={inp} placeholder="e.g. M365 Suite - BB"/>
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color:'#475569' }}>Nature of Expense</label>
                <select value={f('nature_of_expense')} onChange={e=>sf('nature_of_expense',e.target.value)} className={inp}>
                  <option value="">— Select —</option>
                  {NATURES.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Qty</label>
                  <input type="number" value={f('qty')} onChange={e=>sf('qty',e.target.value)} className={inp} placeholder="0"/>
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Unit Price (LKR)</label>
                  <input type="number" value={f('unit_price_lkr')} onChange={e=>sf('unit_price_lkr',e.target.value)} className={inp} placeholder="0"/>
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Q Total (LKR)</label>
                  <input type="number" value={f('budget_q_total_lkr')} onChange={e=>sf('budget_q_total_lkr',e.target.value)} className={inp} placeholder="0"/>
                </div>
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color:'#475569' }}>FY Total (LKR) *</label>
                <input type="number" value={f('budget_fy_total_lkr')} onChange={e=>sf('budget_fy_total_lkr',e.target.value)} className={inp} placeholder="0"/>
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color:'#475569' }}>Business Justification</label>
                <input value={f('business_justification')} onChange={e=>sf('business_justification',e.target.value)} className={inp} placeholder="Why is this needed?"/>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={saveItem} disabled={saving} className="btn-primary flex-1 text-sm">{saving?'Saving...':'Save Item'}</button>
              <button onClick={() => setModal(null)} className="btn-ghost flex-1 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {modal==='actual' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth:520 }}>
            <h2 className="text-base font-bold mb-1" style={{ color:'#e2e8f0' }}>{f('id') ? 'Edit' : 'Add'} Actual Expense</h2>
            <div className="text-xs mb-4" style={{ color:'#334155' }}>Asset purchases are auto-linked. Use this for renewals, services, and other manual expenses.</div>
            {error && <div className="mb-3 p-2 rounded-lg text-xs" style={{ background:'rgba(239,68,68,0.1)', color:'#f87171' }}>{error}</div>}
            <div className="space-y-3">
              <div>
                <label className="block text-xs mb-1" style={{ color:'#475569' }}>Link to Budget Item *</label>
                <select value={f('budget_item_id')} onChange={e=>sf('budget_item_id',e.target.value)} className={inp}
                  style={{ borderColor: !f('budget_item_id') ? 'rgba(239,68,68,0.4)' : undefined }}>
                  <option value="">— Select a budget item —</option>
                  <option value={OUT_OF_BUDGET} style={{ color:'#fb923c' }}>⚠ Out of Budget / Extra (not in budget)</option>
                  {items.map(i => (
                    <option key={i.id} value={i.id}>{i.expenditure_type} {i.category} — {i.item}</option>
                  ))}
                </select>
                {f('budget_item_id') === OUT_OF_BUDGET && (
                  <div className="mt-1 text-xs" style={{ color:'#fb923c' }}>This expense is not budgeted. It will be tracked separately as an over-budget / extra cost.</div>
                )}
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color:'#475569' }}>Description *</label>
                <input value={f('description')} onChange={e=>sf('description',e.target.value)} className={inp} placeholder="e.g. M365 Q1 renewal payment"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Amount (LKR)</label>
                  <input type="number" value={f('amount_lkr')} onChange={e=>sf('amount_lkr',e.target.value)} className={inp} placeholder="0"/>
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Expense Date *</label>
                  <input type="date" value={f('expense_date')} onChange={e=>sf('expense_date',e.target.value)} className={inp}/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Type</label>
                  <select value={f('expenditure_type')} onChange={e=>sf('expenditure_type',e.target.value)} className={inp}>
                    {EXP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Category</label>
                  <select value={f('category')} onChange={e=>sf('category',e.target.value)} className={inp}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color:'#475569' }}>Notes</label>
                <input value={f('notes')} onChange={e=>sf('notes',e.target.value)} className={inp} placeholder="Invoice ref, payment details, etc."/>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={saveActual} disabled={saving} className="btn-primary flex-1 text-sm">{saving?'Saving...':'Save'}</button>
              <button onClick={() => setModal(null)} className="btn-ghost flex-1 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {newYearModal && (
        <div className="modal-overlay" onClick={() => setNewYearModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth:400 }}>
            <h2 className="text-base font-bold mb-4" style={{ color:'#e2e8f0' }}>New Budget Year</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs mb-1" style={{ color:'#475569' }}>FY Label *</label>
                <input value={newYearForm.fy_label} onChange={e=>setNewYearForm(p=>({...p,fy_label:e.target.value}))} className={inp} placeholder="e.g. 2027/28"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Start Date *</label>
                  <input type="date" value={newYearForm.fy_start} onChange={e=>setNewYearForm(p=>({...p,fy_start:e.target.value}))} className={inp}/>
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>End Date *</label>
                  <input type="date" value={newYearForm.fy_end} onChange={e=>setNewYearForm(p=>({...p,fy_end:e.target.value}))} className={inp}/>
                </div>
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color:'#475569' }}>Notes</label>
                <input value={newYearForm.notes} onChange={e=>setNewYearForm(p=>({...p,notes:e.target.value}))} className={inp} placeholder="Optional notes"/>
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color:'#475569' }}>Start from previous year</label>
                <select value={newYearForm.copy_from_year_id||''} onChange={e=>setNewYearForm(p=>({...p,copy_from_year_id:e.target.value||undefined}))} className={inp}>
                  <option value="">Blank — no items</option>
                  {years.map(y => <option key={y.id} value={y.id}>Copy all budget items from FY {y.fy_label}</option>)}
                </select>
                <div className="text-xs mt-1" style={{ color:'#334155' }}>Copies the full line-item structure and amounts as a starting point. Actuals are never copied.</div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={createYear} className="btn-primary flex-1 text-sm">Create</button>
              <button onClick={() => setNewYearModal(false)} className="btn-ghost flex-1 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Backfill modal */}
      {backfillModal && (
        <div className="modal-overlay" onClick={() => { if (!backfillLoading) setBackfillModal(false); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth:560 }}>
            <h2 className="text-base font-bold mb-1" style={{ color:'#e2e8f0' }}>Import Existing Assets & Accessories</h2>
            <div className="text-xs mb-4" style={{ color:'#334155' }}>
              Scans all assets and accessories with a recorded purchase price and date, then creates budget actual entries for any not already imported. Uses the purchase date to determine the financial year. Safe to run multiple times — duplicates are skipped.
            </div>

            {backfillLoading && (
              <div className="flex items-center justify-center gap-3 py-8">
                <div className="w-5 h-5 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin"/>
                <span className="text-sm" style={{ color:'#818cf8' }}>{backfillResult ? 'Importing...' : 'Scanning...'}</span>
              </div>
            )}

            {!backfillLoading && backfillResult && (
              <div>
                {backfillResult.error ? (
                  <div className="p-4 rounded-xl text-sm" style={{ background:'rgba(239,68,68,0.1)', color:'#f87171' }}>{backfillResult.error}</div>
                ) : (
                  <div className="p-4 rounded-xl space-y-2" style={{ background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.2)' }}>
                    <div className="flex items-center gap-2 text-sm font-semibold" style={{ color:'#34d399' }}>
                      <CheckCircle size={16}/> Import complete
                    </div>
                    <div className="text-xs" style={{ color:'#94a3b8' }}>
                      <span className="font-semibold" style={{ color:'#e2e8f0' }}>{backfillResult.inserted}</span> records imported · <span className="font-semibold" style={{ color:'#475569' }}>{backfillResult.skipped}</span> skipped (no matching budget year)
                    </div>
                    <div className="text-xs mt-2" style={{ color:'#475569' }}>Switch to the Actuals or Budget vs Actual tab to see the imported records. You can link them to specific budget items by editing each actual.</div>
                  </div>
                )}
                <div className="flex justify-end mt-4">
                  <button onClick={() => setBackfillModal(false)} className="btn-primary text-sm">Close</button>
                </div>
              </div>
            )}

            {!backfillLoading && !backfillResult && backfillPreview && (
              <div>
                {backfillPreview.error ? (
                  <div className="p-4 rounded-xl text-sm" style={{ background:'rgba(239,68,68,0.1)', color:'#f87171' }}>{backfillPreview.error}</div>
                ) : backfillPreview.total === 0 ? (
                  <div className="p-4 rounded-xl flex items-center gap-3" style={{ background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.2)' }}>
                    <CheckCircle size={16} style={{ color:'#34d399' }}/>
                    <span className="text-sm" style={{ color:'#34d399' }}>All assets and accessories are already imported. Nothing to do.</span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Summary counts */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 rounded-xl text-center" style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)' }}>
                        <div className="text-xl font-bold" style={{ color:'#60a5fa' }}>{backfillPreview.total}</div>
                        <div className="text-xs mt-0.5" style={{ color:'#475569' }}>Total to import</div>
                      </div>
                      <div className="p-3 rounded-xl text-center" style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)' }}>
                        <div className="text-xl font-bold" style={{ color:'#818cf8' }}>{backfillPreview.assets}</div>
                        <div className="text-xs mt-0.5" style={{ color:'#475569' }}>Assets</div>
                      </div>
                      <div className="p-3 rounded-xl text-center" style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)' }}>
                        <div className="text-xl font-bold" style={{ color:'#34d399' }}>{backfillPreview.accessories}</div>
                        <div className="text-xs mt-0.5" style={{ color:'#475569' }}>Accessories</div>
                      </div>
                    </div>

                    {/* By FY breakdown */}
                    <div className="rounded-xl overflow-hidden" style={{ border:'1px solid rgba(255,255,255,0.08)' }}>
                      <div className="px-4 py-2 text-xs font-semibold" style={{ background:'rgba(255,255,255,0.04)', color:'#475569' }}>Breakdown by Financial Year</div>
                      {Object.entries(backfillPreview.byFY).map(([fy, counts]) => (
                        <div key={fy} className="flex items-center justify-between px-4 py-2.5" style={{ borderTop:'1px solid rgba(255,255,255,0.05)' }}>
                          <span className="text-sm font-semibold" style={{ color:'#e2e8f0' }}>FY {fy}</span>
                          <div className="flex gap-4 text-xs">
                            {counts.assets > 0 && <span style={{ color:'#818cf8' }}>🖥 {counts.assets} assets</span>}
                            {counts.accessories > 0 && <span style={{ color:'#34d399' }}>🔌 {counts.accessories} accessories</span>}
                            {counts.noYear > 0 && <span style={{ color:'#f59e0b' }}>⚠ {counts.noYear} no budget year (will skip)</span>}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* First 10 preview */}
                    {backfillPreview.rows?.length > 0 && (
                      <div>
                        <div className="text-xs mb-1.5" style={{ color:'#475569' }}>Preview (first {Math.min(10, backfillPreview.rows.length)} of {backfillPreview.total}):</div>
                        <div className="rounded-xl overflow-hidden" style={{ border:'1px solid rgba(255,255,255,0.08)', maxHeight:200, overflowY:'auto' }}>
                          {backfillPreview.rows.slice(0,10).map((r, i) => (
                            <div key={i} className="flex items-center justify-between px-3 py-2" style={{ borderBottom:'1px solid rgba(255,255,255,0.04)', fontSize:11 }}>
                              <div style={{ flex:1, minWidth:0 }}>
                                <span style={{ color: r.source==='asset'?'#818cf8':'#34d399' }}>{r.source==='asset'?'🖥':'🔌'}</span>
                                <span className="ml-1.5 truncate" style={{ color:'#cbd5e1' }}>{r.description}</span>
                                {r.budgetItemName && <span className="ml-1.5" style={{ color:'#60a5fa' }}>→ {r.budgetItemName}</span>}
                              </div>
                              <div className="flex items-center gap-3 ml-3 shrink-0">
                                <span style={{ color:'#94a3b8' }}>FY {r.fyLabel}</span>
                                <span style={{ color:'#34d399', fontWeight:600 }}>LKR {Number(Math.round(r.amount)).toLocaleString()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex gap-2 mt-4">
                  {backfillPreview.total > 0 && !backfillPreview.error && (
                    <button onClick={runBackfill} className="btn-primary flex-1 text-sm">
                      Import {backfillPreview.total} Record{backfillPreview.total>1?'s':''}
                    </button>
                  )}
                  <button onClick={() => setBackfillModal(false)} className="btn-ghost flex-1 text-sm">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
